import {Database} from '@tnc/shared';
import {useRef, useState} from 'react';
import {useNavigate} from 'react-router';

import {useNotifications} from '@/providers/components/notificationsProvider';

import {Modal, ModalRefT} from '@/components/Modal';
import DefaultTableAvatarImage from '@/components/defaultTableAvatarImage';

import {useOrder} from '../providers/orderDetailsProvider';

// THIS TYPE OVERRIDES THE JSON TYPE THAT COMES BACK FROM METRCPACKAGES
type MetrcPackages = {
  id: number;
  package_label: string;
  assigned_items: number;
  shipped_quantity: number;
  quantity: number;
  received_quantity: number;
  metric_package_id: number;
  linked_product_id: number;
  metrc_item?: {
    id: number;
    metric_item_id: number;
    name: string;
    product_category_name: string;
    product_category_type: string;
  };
}[];

type manifestKanbanTaskPropsT = {
  item: Omit<
    Database['public']['Functions']['get_metrc_transfers_with_package_counts']['Returns'][number],
    'metric_packages'
  > & {
    metric_packages: MetrcPackages;
  };
  isDark?: boolean;
};

function ManifestKanbanTask({item: transfer, isDark}: manifestKanbanTaskPropsT) {
  // Hooks
  const {toast} = useNotifications();
  const navigate = useNavigate();
  const {orderId, orderItems, handleSubmitItemAssignment} = useOrder();

  // State
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<MetrcPackages[0] | null>(null);
  const [assignmentQuantity, setAssignmentQuantity] = useState<number>(0);

  // Refs
  const modalRef = useRef<ModalRefT>(null);

  const handleCardClick = () => {
    navigate(`/admin/orders/${orderId}/transfer-manifest/${transfer.metrc_transfer_id}`, {
      state: {
        packageCount: transfer.package_count,
        itemCount: transfer.total_items_shipped,
      },
    });
  };

  const handleAssignClick = async (productId: number) => {
    if (!selectedPackage || !selectedPackage.metrc_item) {
      toast.error({
        title: 'No package or metrc item selected',
        message: 'Please select a package and metrc item',
      });
      return;
    }

    await handleSubmitItemAssignment({
      productId,
      packageId: selectedPackage.id,
      metrcItemId: selectedPackage.metrc_item?.id,
      quantity: assignmentQuantity,
    });
    modalRef.current?.hide();
    setSelectedPackage(null);
    setSelectedItemId(null);
    setAssignmentQuantity(0);
  };

  const handleModalClose = () => {
    setSelectedPackage(null);
    setSelectedItemId(null);
    setAssignmentQuantity(0);
  };

  return (
    <>
      <div className={`box kanban-tasks !mb-4 ${isDark ? 'px-1' : ''}`}>
        <div className="box custom-box !bg-slate-800">
          <div className="box-header !flex flex-wrap items-center !justify-start">
            <div className="me-2">
              <span className="avatar avatar-rounded bg-primary/10 p-2 text-primary">
                <i className="ri-truck-line text-xl"></i>
              </span>
            </div>
            <div className="flex-grow">
              <div className="text-truncate block text-[.875rem] font-semibold">
                Manifest #{transfer.manifest_number}
              </div>
              <span className="block text-[0.75rem] text-[#8c9097] dark:text-white/50">
                Total <strong className="text-defaulttextcolor">{transfer.total_items_shipped}</strong> items in{' '}
                <strong className="text-defaulttextcolor">{transfer.package_count}</strong> packages
              </span>
            </div>
            <button
              onClick={handleCardClick}
              className="ti-btn ti-btn-icon ti-btn-wave bg-primary/10 text-primary hover:bg-primary hover:text-white"
            >
              <i className="ri-information-line"></i>
            </button>
          </div>
          <div className="box-body">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="mb-1 font-semibold">Order #{orderId}</div>
              </div>
              <div className="text-end">
                <div className="mb-1 font-semibold">Status</div>
                <span className="badge bg-success/10 text-success">Active</span>
              </div>
            </div>

            {/* PACKAGES */}
            {transfer.metric_packages.map((pkg, index) => {
              const assignedPercentage = Math.round((pkg.assigned_items / pkg.received_quantity) * 100);

              return (
                <div
                  key={pkg.id}
                  className={`mb-5 ${transfer.package_count > 1 && index < transfer.package_count - 1 ? 'border-b border-gray-500/20 pb-5' : ''}`}
                >
                  <div className="mb-1 text-[0.7rem]">{pkg.metrc_item?.name || 'NO METRC ITEM FOUND'}</div>
                  <div className="flex items-center gap-2">
                    <div
                      className="progress progress-xl progress-animate custom-progress-4 bg-success/10"
                      aria-valuenow={assignedPercentage}
                      aria-valuemin={0}
                      aria-valuemax={pkg.received_quantity}
                    >
                      <div
                        className="progress-bar !rounded-sm bg-success"
                        style={{width: `${assignedPercentage}%`}}
                      ></div>
                      <div className="progress-bar-label">{assignedPercentage}%</div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedPackage(pkg);
                        modalRef.current?.show();
                      }}
                      className="ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.25rem] !w-[1.25rem] bg-primary/10 text-[0.7rem] text-primary hover:bg-primary hover:text-white"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className={`badge mt-2 !rounded-sm bg-info/10 text-info`}>
                    PACKAGE: <span className="pl-1 text-white">{pkg.package_label}</span>
                  </span>
                  <span className={`badge ml-1 mt-2 !rounded-sm bg-success/10 text-success`}>
                    ASSIGNED: <span className="pl-1 text-white">{pkg.assigned_items}</span>
                  </span>
                  <span className={`badge ml-1 mt-2 !rounded-sm bg-warning/10 text-warning`}>
                    SHIPPED: <span className="pl-1 text-white">{pkg.shipped_quantity}</span>
                  </span>
                </div>
              );
            })}
          </div>
          <div className="box-footer flex items-center justify-between">
            <div>
              <span className="block text-[0.6875rem] text-[#8c9097] dark:text-white/50">Total Items:</span>
              <span className="block font-semibold">{transfer.total_items_shipped}</span>
            </div>
            <div className="text-end">
              <span className="block text-[0.6875rem] text-[#8c9097] dark:text-white/50">Total Packages:</span>
              <span className="block font-semibold">{transfer.package_count}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ITEM ASSIGNMENT MODAL */}
      <Modal
        size="xl"
        ref={modalRef}
        title={`Assign ${selectedItemId ? orderItems.find(item => item.product_id === selectedItemId)?.product_name : 'Product'}`}
        onClose={handleModalClose}
      >
        <div className="col-span-12 xl:col-span-12">
          <div className="table-responsive">
            <table className="nowrap mt-4 table min-w-full whitespace-nowrap border dark:border-defaultborder/10">
              <thead>
                <tr>
                  <th scope="row" className="text-start">
                    PRODUCT
                  </th>
                  <th scope="row" className="text-start">
                    TOTAL
                  </th>
                  <th scope="row" className="text-start">
                    ASSIGNED
                  </th>
                  <th scope="row" className="text-start">
                    UNASSIGNED
                  </th>
                  <th scope="row" className="text-start">
                    AVAILABLE
                  </th>
                  <th scope="row" className="text-start">
                    QUANTITY
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderItems
                  .filter(
                    item =>
                      (item.linked_metric_item_id && item.linked_metric_item_id === selectedPackage?.metrc_item?.id) ||
                      (!item.linked_metric_item_id && selectedPackage?.linked_product_id === item.product_id) ||
                      (!item.linked_metric_item_id && !selectedPackage?.linked_product_id)
                  )
                  .map((item, index) => {
                    const unassignedCount = item.product_count - item.metrc_linked_count;
                    const isSelected = selectedItemId === item.product_id;
                    const packageAvailable = selectedPackage
                      ? selectedPackage.received_quantity - selectedPackage.assigned_items
                      : 0;

                    return (
                      <tr key={index} className="border border-defaultborder dark:border-defaultborder/10">
                        <td>
                          <div className="flex min-w-[150px] gap-2">
                            <DefaultTableAvatarImage
                              src={item.product_image_url || item.brand_logo_url || '/images/logo/logo.png'}
                              alt="Product Logo"
                            />
                            <div className="flex flex-col">
                              <span>{item.product_name}</span>
                              <span className="text-muted text-truncate text-gray text-xs text-textmuted">
                                {item.brand_name}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="product-quantity-container text-center">{item.product_count}</td>
                        <td className="product-quantity-container text-center">{item.metrc_linked_count}</td>
                        <td className="product-quantity-container text-center">{unassignedCount}</td>
                        <td className="product-quantity-container text-center">{packageAvailable}</td>
                        <td className="product-quantity-container">
                          {isSelected ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min={0}
                                max={Math.min(unassignedCount, packageAvailable)}
                                value={assignmentQuantity}
                                onChange={e =>
                                  setAssignmentQuantity(
                                    Math.min(
                                      Math.min(unassignedCount, packageAvailable),
                                      Math.max(0, parseInt(e.target.value) || 0)
                                    )
                                  )
                                }
                                className="form-control !w-16 !text-base"
                              />
                              <button
                                className="ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.25rem] !w-[1.25rem] bg-success/10 text-[0.7rem] text-success hover:bg-success hover:text-white"
                                onClick={() => handleAssignClick(item.product_id)}
                              >
                                <i className="ri-check-line"></i>
                              </button>
                            </div>
                          ) : (
                            <button
                              className="ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.25rem] !w-[1.25rem] bg-primary/10 text-[0.7rem] text-primary hover:bg-primary hover:text-white"
                              onClick={() => setSelectedItemId(item.product_id)}
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </>
  );
}
export default ManifestKanbanTask;
